import { CheckCircle2, Clock3, Mic, Play, RotateCcw, Square, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NextActionScheduler } from "./components";
import { DebriefGateCard } from "./DebriefGateCard";
import { DEBRIEF_REASON_LABELS, shotScoreReviewFromAnalysis } from "./debrief";
import { nextManualStepDraft } from "./nextActionEngine";
import { nextStepDraftFromAnalysis } from "./debriefIntelligence";
import { MEETING_OUTCOMES, meetingOutcomeNextStep, type MeetingOutcome } from "./meetingOutcome";
import { preferredVoiceMimeType, validateVoiceDebrief, VOICE_DEBRIEF_MAX_SECONDS } from "./voiceDebrief";
import type { DebriefAnalysis, DebriefCrmFact, DebriefNotRequiredReason, DebriefReminder, RealEstateCase } from "./types";
import type { NextStepDraft } from "./uiTypes";

export type DebriefItem = { caseItem: RealEstateCase; reminder: DebriefReminder };
export type DebriefShotScoreReview = {
  identity: boolean;
  value: boolean;
  acquisition: boolean;
  nextStep: boolean;
};

export function DebriefInbox({ items, now, onStart, onSnooze, onNotRequired }: {
  items: DebriefItem[];
  now: Date;
  onStart: (caseId: string, reminderId: string) => void;
  onSnooze: (caseId: string, reminderId: string, minutes: number) => void;
  onNotRequired: (caseId: string, reminderId: string) => void;
}) {
  if (!items.length) return null;
  return (
    <section className="debrief-inbox" aria-labelledby="debrief-inbox-title">
      <div className="debrief-inbox-heading">
        <div><p className="eyebrow">Po stretnutí</p><h2 id="debrief-inbox-title">Debriefy na dokončenie</h2></div>
        <span>{items.length}</span>
      </div>
      {items.map(({ caseItem, reminder }) => (
        <DebriefGateCard
          key={reminder.id}
          caseItem={caseItem}
          reminder={reminder}
          now={now}
          onStart={onStart}
          onSnooze={onSnooze}
          onNotRequired={onNotRequired}
        />
      ))}
    </section>
  );
}

export function DebriefSheet({ item, busy, error, today, requiresNextStep, existingNextStep, onClose, onAnalyze, onAnalyzeAudio, onDraftChange, onConfirm, onManualOutcome, onNotRequired, onMeetingDidNotHappen }: {
  item: DebriefItem;
  busy: boolean;
  error?: string;
  today: string;
  requiresNextStep: boolean;
  existingNextStep?: string;
  onClose: () => void;
  onAnalyze: (text: string) => void;
  onAnalyzeAudio: (audio: Blob, durationSeconds: number) => void;
  onDraftChange: (text: string) => void;
  onConfirm: (analysis: DebriefAnalysis, text: string, shotScore: DebriefShotScoreReview, approvedCrmFacts: DebriefCrmFact[], nextStep?: NextStepDraft) => void;
  onManualOutcome: (outcome: MeetingOutcome, nextStep?: NextStepDraft) => void;
  onNotRequired: (reason: DebriefNotRequiredReason, note: string) => void;
  onMeetingDidNotHappen: (reason: DebriefNotRequiredReason, note: string, reschedule: boolean) => void;
}) {
  const { caseItem, reminder } = item;
  const [text, setText] = useState(reminder.debriefText ?? "");
  const [inputMode, setInputMode] = useState<"voice" | "text">(reminder.inputMode ?? "voice");
  const [recording, setRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [audio, setAudio] = useState<Blob>();
  const [audioUrl, setAudioUrl] = useState<string>();
  const [recordingError, setRecordingError] = useState<string>();
  const recorderRef = useRef<MediaRecorder>();
  const streamRef = useRef<MediaStream>();
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const sheetRef = useRef<HTMLElement>(null);
  const [showReasons, setShowReasons] = useState(false);
  const [showMissedMeeting, setShowMissedMeeting] = useState(false);
  const [reason, setReason] = useState<DebriefNotRequiredReason>("meeting_cancelled");
  const [reasonNote, setReasonNote] = useState("");
  const [missedResolution, setMissedResolution] = useState<"reschedule" | "cancelled" | "no_show" | "other">("reschedule");
  const [missedNote, setMissedNote] = useState("");
  const [showManualOutcome, setShowManualOutcome] = useState(false);
  const [manualOutcome, setManualOutcome] = useState<MeetingOutcome>();
  const [manualNextStep, setManualNextStep] = useState<NextStepDraft>();
  const [shotScore, setShotScore] = useState<DebriefShotScoreReview>(() => shotScoreReviewFromAnalysis(reminder.analysis));
  const [approvedFactIds, setApprovedFactIds] = useState<string[]>(() => reminder.analysis?.crmFacts?.map((fact) => fact.id) ?? []);
  const [recommendationApplied, setRecommendationApplied] = useState(Boolean(reminder.analysis?.shotScoreRecommendation));
  const initialNextStep = reminder.analysis ? nextStepDraftFromAnalysis(reminder.analysis) : nextManualStepDraft(new Date());
  const [nextStep, setNextStep] = useState<NextStepDraft>({ ...initialNextStep, purpose: "" });
  const analysis = reminder.analysis;
  const transcriptChanged = Boolean(analysis) && text.trim() !== (reminder.debriefText ?? "").trim();
  const voiceSupported = typeof MediaRecorder !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
  const score = [shotScore.identity, shotScore.value, shotScore.acquisition, shotScore.nextStep].filter(Boolean).length * 25;
  const scoreDimensions: Array<{ key: keyof DebriefShotScoreReview; title: string; body: string }> = [
    { key: "identity", title: "Identita", body: "Klient vie, že robím reality + financie" },
    { key: "value", title: "Hodnota", body: "Ponúkol som konkrétnu hodnotu alebo službu" },
    { key: "acquisition", title: "Náber", body: "Otestoval som spoluprácu alebo náber" },
    { key: "nextStep", title: "Next step", body: "Dohodol som ďalší krok alebo dostal jasné nie" }
  ];

  useEffect(() => {
    if (!analysis) return;
    setNextStep((current) => current.purpose.trim() ? current : nextStepDraftFromAnalysis(analysis));
    setApprovedFactIds((current) => current.length ? current : analysis.crmFacts?.map((fact) => fact.id) ?? []);
  }, [analysis]);

  useEffect(() => {
    if (!analysis?.shotScoreRecommendation || recommendationApplied) return;
    setShotScore(shotScoreReviewFromAnalysis(analysis));
    setRecommendationApplied(true);
  }, [analysis, recommendationApplied]);

  useEffect(() => {
    if (reminder.inputMode === "voice" && reminder.debriefText && reminder.debriefText !== text) {
      setText(reminder.debriefText);
    }
  }, [reminder.debriefText, reminder.inputMode]);

  useEffect(() => {
    if (inputMode !== "text" || analysis) return;
    if (text === (reminder.debriefText ?? "")) return;
    const timeout = window.setTimeout(() => onDraftChange(text), 450);
    return () => window.clearTimeout(timeout);
  }, [analysis, inputMode, onDraftChange, reminder.debriefText, text]);

  useEffect(() => {
    if (!audio) {
      setAudioUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(audio);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audio]);

  useEffect(() => {
    if (!recording) return;
    const interval = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1_000);
      setElapsedSeconds(elapsed);
      if (elapsed >= VOICE_DEBRIEF_MAX_SECONDS && recorderRef.current?.state === "recording") recorderRef.current.stop();
    }, 250);
    return () => window.clearInterval(interval);
  }, [recording]);

  useEffect(() => () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    sheetRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, []);

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const startRecording = async () => {
    setRecordingError(undefined);
    setAudio(undefined);
    if (!voiceSupported) {
      setRecordingError("Nahrávanie hlasu tento prehliadač nepodporuje. Použi textový debrief.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mimeType = preferredVoiceMimeType((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      stream.getAudioTracks().forEach((track) => { track.onended = () => stopRecording(); });
      chunksRef.current = [];
      recorder.ondataavailable = (event) => { if (event.data.size) chunksRef.current.push(event.data); };
      recorder.onstop = () => {
        const duration = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1_000));
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || "audio/webm" });
        setElapsedSeconds(duration);
        setRecording(false);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = undefined;
        const validationError = validateVoiceDebrief(blob, duration);
        if (validationError) setRecordingError(validationError);
        else setAudio(blob);
      };
      recorder.onerror = () => {
        setRecordingError("Nahrávanie sa prerušilo. Skús to znova alebo použi text.");
        setRecording(false);
        stream.getTracks().forEach((track) => track.stop());
      };
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      recorder.start(1_000);
      setRecording(true);
    } catch {
      setRecordingError("Mikrofón nebol povolený. Povoľ ho v nastaveniach alebo použi textový debrief.");
    }
  };

  const formatDuration = (seconds: number) => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const approvedFacts = analysis?.crmFacts?.filter((fact) => approvedFactIds.includes(fact.id)) ?? [];

  return <div className="now-sheet-backdrop" onClick={onClose}>
    <section ref={sheetRef} tabIndex={-1} className="debrief-sheet" role="dialog" aria-modal="true" aria-labelledby="debrief-sheet-title" onClick={(event) => event.stopPropagation()}>
      <header><div><p className="eyebrow">{reminder.eventLabel}</p><h2 id="debrief-sheet-title">AI debrief · {caseItem.contactName}</h2></div><button className="icon-button" type="button" onClick={onClose}><X size={18} /><span className="sr-only">Zavrieť</span></button></header>
      {!analysis ? <>
        <p className="debrief-coaching">Čo bolo dobré, čo si zmeškal a čo má byť ďalší krok?</p>
        {inputMode === "voice" ? <div className="debrief-recorder">
          <div className={`debrief-recorder-state${recording ? " recording" : ""}`} aria-live="polite">
            <span className="debrief-mic"><Mic size={21} /></span>
            <div><strong>{recording ? "Nahrávam debrief" : audio ? "Nahrávka pripravená" : "Hlasový debrief"}</strong><small>{recording || audio ? formatDuration(elapsedSeconds) : "Nahraj prirodzené zhrnutie stretnutia."}</small></div>
          </div>
          {audioUrl ? <audio className="debrief-audio" controls preload="metadata" src={audioUrl}>Prehrávač zvuku nie je podporovaný.</audio> : null}
          {recording ? <button className="primary-button full-button" type="button" onClick={stopRecording}><Square size={17} /> Zastaviť nahrávanie</button>
            : audio ? <div className="debrief-recorder-actions">
              <button className="secondary-button" type="button" onClick={() => { setAudio(undefined); setElapsedSeconds(0); }}><RotateCcw size={16} /> Nahrať znova</button>
              <button className="primary-button" type="button" disabled={busy} onClick={() => onAnalyzeAudio(audio, elapsedSeconds)}><Play size={16} /> {busy ? "Prepisujem…" : "Prepísať a analyzovať"}</button>
            </div>
            : <button className="primary-button full-button" type="button" onClick={() => void startRecording()}><Mic size={18} /> Nahrať debrief</button>}
          {!voiceSupported ? <p className="debrief-hint">Hlas nie je v tomto prehliadači podporovaný.</p> : null}
        </div> : <label className="field"><span>Textový debrief</span><textarea rows={7} value={text} onChange={(event) => setText(event.target.value)} placeholder="Zapíš priebeh, reakcie klienta, námietky a dohodnutý ďalší krok." /></label>}
        <button className="text-button debrief-mode-switch" type="button" disabled={recording} onClick={() => setInputMode((mode) => mode === "voice" ? "text" : "voice")}>{inputMode === "voice" ? "Namiesto nahrávky napísať debrief" : "Použiť hlasový debrief"}</button>
        {recordingError ? <p className="form-error" role="alert">{recordingError}</p> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        {inputMode === "text" ? <button className="primary-button full-button" type="button" disabled={busy || text.trim().length < 20} onClick={() => onAnalyze(text.trim())}>{busy ? "Analyzujem…" : "Analyzovať debrief"}</button> : null}
      </> : <>
        <label className="field debrief-transcript"><span>{reminder.inputMode === "voice" ? "Prepis nahrávky" : "Debrief"}</span><textarea rows={5} value={text} onChange={(event) => setText(event.target.value)} /></label>
        {transcriptChanged ? <div className="debrief-reanalysis" role="status"><p>Prepis bol upravený. Pred potvrdením ho znova analyzuj.</p><button className="secondary-button" type="button" disabled={busy || text.trim().length < 20} onClick={() => onAnalyze(text.trim())}>{busy ? "Analyzujem…" : "Znovu analyzovať"}</button></div> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="debrief-analysis"><h3>Zhrnutie</h3><p>{analysis.summary}</p><h3>Čo fungovalo</h3><ul>{analysis.positives.map((value) => <li key={value}>{value}</li>)}</ul><h3>Čo mohlo uniknúť</h3><ul>{analysis.missed.map((value) => <li key={value}>{value}</li>)}</ul>{analysis.risks?.length ? <><h3>Riziká</h3><ul>{analysis.risks.map((value) => <li key={value}>{value}</li>)}</ul></> : null}<h3>Odporúčaný ďalší krok</h3><p>{analysis.nextStep}</p><p className="debrief-coaching">{analysis.coaching}</p></div>
        {analysis.objections?.length ? <details className="debrief-details"><summary>Námietky klienta ({analysis.objections.length})</summary>{analysis.objections.map((objection) => <div key={`${objection.category}-${objection.wording}`}><strong>{objection.wording}</strong><small>{objection.evidence}</small></div>)}</details> : null}
        {analysis.crmFacts?.length ? <fieldset className="debrief-crm-facts"><legend>Fakty na uloženie do debriefu</legend>{analysis.crmFacts.map((fact) => <label key={fact.id}><input type="checkbox" checked={approvedFactIds.includes(fact.id)} onChange={(event) => setApprovedFactIds((current) => event.target.checked ? [...new Set([...current, fact.id])] : current.filter((id) => id !== fact.id))} /><span><strong>{fact.label}</strong><small>{fact.value}</small><em>Dôkaz: {fact.evidence}</em></span></label>)}</fieldset> : null}
        {analysis.playbookSignals?.length ? <details className="debrief-details"><summary>Kontrola meeting playbooku</summary>{analysis.playbookSignals.map((signal) => <div key={signal.key}><strong>{signal.label}</strong><span className={`debrief-signal ${signal.status}`}>{signal.status === "confirmed" ? "Potvrdené" : signal.status === "missed" ? "Chýbalo" : "Nejasné"}</span><small>{signal.evidence}</small></div>)}</details> : null}
        <div className="debrief-shot-score">
          <div><span>Shot Score{analysis.shotScoreRecommendation ? " · AI návrh na kontrolu" : ""}</span><strong>{score} %</strong></div>
          <div className="shot-score-options">
            {scoreDimensions.map((dimension) => {
              const recommendation = analysis.shotScoreRecommendation?.[dimension.key];
              return <div className="debrief-score-row" key={dimension.key}>
                <span>
                  <strong>{dimension.title}</strong>
                  <small>{dimension.body}</small>
                  {recommendation ? <em>{recommendation.evidence}</em> : null}
                </span>
                <div className="debrief-binary" role="group" aria-label={`${dimension.title}: výsledok`}><button className={shotScore[dimension.key] ? "active" : ""} type="button" onClick={() => setShotScore({ ...shotScore, [dimension.key]: true })}>Áno</button><button className={!shotScore[dimension.key] ? "active" : ""} type="button" onClick={() => setShotScore({ ...shotScore, [dimension.key]: false })}>Nie</button></div>
              </div>;
            })}
          </div>
        </div>
        {requiresNextStep ? <div className="debrief-next-step">
          <div className="progressive-heading"><p className="eyebrow">Ďalší krok</p><h3>Čo má nasledovať?</h3></div>
          <NextActionScheduler
            today={today}
            value={nextStep}
            onChange={setNextStep}
            onCancel={onClose}
            onSave={() => { if (!transcriptChanged) onConfirm(analysis, text, shotScore, approvedFacts, nextStep); }}
            saveLabel="Potvrdiť debrief a naplánovať"
            saveDisabled={transcriptChanged}
          />
        </div> : <>
          {existingNextStep ? <p className="debrief-existing-next-step"><strong>Ďalší krok:</strong> {existingNextStep}</p> : null}
          <button className="primary-button full-button" type="button" disabled={transcriptChanged} onClick={() => onConfirm(analysis, text, shotScore, approvedFacts)}><CheckCircle2 size={17} /> Potvrdiť a uložiť debrief</button>
        </>}
      </>}
      {!analysis ? <>
        <button className="secondary-button full-button" type="button" onClick={() => { setShowManualOutcome((value) => !value); setShowMissedMeeting(false); setShowReasons(false); }}>
          Vybrať výsledok stretnutia manuálne
        </button>
        {showManualOutcome ? <div className="debrief-reason">
          <label className="field">
            <span>Výsledok stretnutia</span>
            <select value={manualOutcome ?? ""} onChange={(event) => {
              const outcome = event.target.value as MeetingOutcome;
              setManualOutcome(outcome || undefined);
              setManualNextStep(outcome ? meetingOutcomeNextStep(outcome) : undefined);
            }}>
              <option value="">Vyber výsledok</option>
              {MEETING_OUTCOMES.map((outcome) => <option key={outcome} value={outcome}>{outcome}</option>)}
            </select>
          </label>
          {manualOutcome === "Nezáujem / uzavrieť prípad" ? (
            <button className="danger-button full-button" type="button" onClick={() => onManualOutcome(manualOutcome)}>Uzavrieť prípad</button>
          ) : manualOutcome && manualNextStep ? (
            <NextActionScheduler
              today={today}
              value={manualNextStep}
              onChange={setManualNextStep}
              onCancel={() => { setShowManualOutcome(false); setManualOutcome(undefined); setManualNextStep(undefined); }}
              onSave={() => onManualOutcome(manualOutcome, manualNextStep)}
              saveLabel="Uložiť výsledok a ďalší krok"
            />
          ) : null}
        </div> : null}
      </> : null}
      <button className="secondary-button full-button" type="button" onClick={() => { setShowMissedMeeting((value) => !value); setShowReasons(false); }}>Stretnutie sa neuskutočnilo</button>
      {showMissedMeeting ? <div className="debrief-reason">
        <div className="debrief-resolution-options" role="group" aria-label="Ako vyriešiť neuskutočnené stretnutie">
          {([
            ["reschedule", "Preložiť / nový termín"],
            ["cancelled", "Zrušené"],
            ["no_show", "Klient neprišiel"],
            ["other", "Iné"]
          ] as const).map(([value, label]) => <button className={missedResolution === value ? "active" : ""} type="button" key={value} onClick={() => setMissedResolution(value)}>{label}</button>)}
        </div>
        {missedResolution === "other" ? <label className="field"><span>Krátky dôvod</span><input value={missedNote} onChange={(event) => setMissedNote(event.target.value)} required /></label> : null}
        <button className="primary-button full-button" type="button" disabled={missedResolution === "other" && !missedNote.trim()} onClick={() => {
          const reason: DebriefNotRequiredReason = missedResolution === "reschedule" ? "meeting_rescheduled" : missedResolution === "cancelled" ? "meeting_cancelled" : missedResolution === "no_show" ? "client_no_show" : "other";
          onMeetingDidNotHappen(reason, missedNote.trim(), missedResolution === "reschedule");
        }}>{missedResolution === "reschedule" ? "Otvoriť nový termín" : "Uzavrieť bez debriefu"}</button>
      </div> : null}
      <button className="text-button debrief-not-required" type="button" onClick={() => setShowReasons((value) => !value)}>Označiť ako nepotrebný</button>
      {showReasons ? <div className="debrief-reason"><label className="field"><span>Dôvod</span><select value={reason} onChange={(event) => setReason(event.target.value as DebriefNotRequiredReason)}>{Object.entries(DEBRIEF_REASON_LABELS).filter(([value]) => value !== "meeting_rescheduled" && value !== "client_no_show").map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label className="field"><span>Krátka poznámka</span><input value={reasonNote} onChange={(event) => setReasonNote(event.target.value)} /></label><button className="secondary-button full-button" type="button" onClick={() => onNotRequired(reason, reasonNote.trim())}><Clock3 size={16} /> Uložiť dôvod</button></div> : null}
    </section>
  </div>;
}
